import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/core/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import type {
  PayrollRosterField,
  RosterFieldFilter,
  RosterFieldValueSummary,
} from "@/payroll/roster/types";
import { ROSTER_FILTER_KIND } from "@/payroll/roster/types";
import {
  isRosterFilterActive,
  ROSTER_NO_VALUE_LABEL,
  rosterFilterDetail,
  rosterFilterSummary,
} from "@/payroll/roster/utils";
import {
  BooleanFilterBody,
  OptionsFilterBody,
  RangeFilterBody,
  TextFilterBody,
} from "./filter-bodies";
import { FilterCheckRow } from "./filter-control-parts";

interface RosterFieldFilterChipProps {
  field: PayrollRosterField;
  options: string[];
  summary: RosterFieldValueSummary | undefined;
  filter: RosterFieldFilter;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (filter: RosterFieldFilter) => void;
  onClear: () => void;
}

export const RosterFieldFilterChip = ({
  field,
  options,
  summary,
  filter,
  open,
  onOpenChange,
  onChange,
  onClear,
}: RosterFieldFilterChipProps) => {
  const active = isRosterFilterActive(filter);
  const bodyProps = { field, options, summary, onChange };
  const canFilterBlank = (summary?.blank ?? 0) > 0 || filter.includeBlank;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Stack
        direction="row"
        align="center"
        gap="none"
        className={cn("h-9 shrink-0 rounded-full border transition-colors", {
          "border-stroke-interactive bg-green-5": active,
          "border-stroke-secondary bg-surface-primary hover:border-stroke-primary":
            !active,
        })}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            title={active ? rosterFilterDetail(field, filter) : undefined}
            className={cn(
              "flex h-9 min-w-0 items-center gap-1.5 rounded-full pl-3 pr-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring",
              { "pr-1": active },
            )}
          >
            <span className="shrink-0 text-secondary-text">{field.label}</span>
            {active ? (
              <span className="min-w-0 max-w-[13rem] truncate font-medium text-primary-text">
                {rosterFilterSummary(field, filter)}
              </span>
            ) : (
              <ChevronDownIcon className="size-3.5 shrink-0 text-tertiary-text" />
            )}
          </button>
        </PopoverTrigger>
        {active && (
          <button
            type="button"
            aria-label={`Clear ${field.label} filter`}
            onClick={onClear}
            className="mr-1.5 flex size-5 shrink-0 items-center justify-center rounded-full text-secondary-text transition-colors hover:bg-surface-secondary hover:text-primary-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <XMarkIcon className="size-3.5" />
          </button>
        )}
      </Stack>

      <PopoverContent align="start" className="w-72 p-0">
        <Stack gap="none" fullWidth>
          <Stack
            direction="row"
            align="center"
            justify="between"
            gap="sm"
            fullWidth
            className="border-b border-stroke-tertiary px-3 py-2"
          >
            <Typography variant="subtitleW3" className="min-w-0 truncate">
              {field.label}
            </Typography>
            {active && (
              <Button size="xs" variant="text" onClick={onClear}>
                Clear
              </Button>
            )}
          </Stack>

          {filter.kind === ROSTER_FILTER_KIND.options ? (
            <OptionsFilterBody {...bodyProps} filter={filter} />
          ) : filter.kind === ROSTER_FILTER_KIND.boolean ? (
            <BooleanFilterBody {...bodyProps} filter={filter} />
          ) : filter.kind === ROSTER_FILTER_KIND.range ? (
            <RangeFilterBody {...bodyProps} filter={filter} />
          ) : (
            <TextFilterBody {...bodyProps} filter={filter} />
          )}

          {canFilterBlank && (
            <Stack
              gap="none"
              fullWidth
              className="border-t border-stroke-tertiary p-1"
            >
              <FilterCheckRow
                id={`roster-filter-${field.key}-no-value`}
                label={ROSTER_NO_VALUE_LABEL}
                count={summary?.blank ?? 0}
                checked={filter.includeBlank}
                onToggle={() =>
                  onChange({ ...filter, includeBlank: !filter.includeBlank })
                }
              />
            </Stack>
          )}
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
